import { useEffect, useRef, useState } from "react";
import { Phone, ArrowRight } from "lucide-react";
import { salonInfo } from "@/mocks/content";

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

export default function CtaSection() {
  const { ref, inView } = useInView(0.2);
  const [hovered, setHovered] = useState(false);

  return (
    <section
      id="cta"
      className="relative py-24 md:py-40 overflow-hidden"
      style={{ background: "#080808" }}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20luxury%20spa%20background%20with%20dark%20moody%20atmosphere%2C%20soft%20golden%20bokeh%20lights%2C%20dark%20stone%20texture%2C%20warm%20amber%20glow%2C%20minimalist%20japanese%20aesthetic%2C%20deep%20black%20background%20with%20subtle%20gold%20particles%2C%20cinematic%20and%20elegant&width=1920&height=800&seq=cta1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
          style={{ opacity: 0.25, filter: "brightness(0.6)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080808] via-transparent to-[#080808]" />
      </div>

      <div
        ref={ref}
        className="relative max-w-3xl mx-auto px-6 text-center"
      >
        {/* Label */}
        <div
          className="flex items-center justify-center gap-4 mb-8"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.8s 0.1s cubic-bezier(0.23, 1, 0.32, 1)",
          }}
        >
          <span className="w-12 h-px bg-[#D4A373]" />
          <span
            className="text-[#D4A373] text-xs tracking-[0.4em] uppercase"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            Reservation
          </span>
          <span className="w-12 h-px bg-[#D4A373]" />
        </div>

        {/* Headline */}
        <h2
          className="text-white mb-4 whitespace-nowrap"
          style={{
            fontFamily: "'Noto Serif JP', serif",
            fontSize: "clamp(1.4rem, 4vw, 3.2rem)",
            letterSpacing: "0.08em",
            lineHeight: 1.4,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(25px)",
            transition: "all 0.8s 0.2s cubic-bezier(0.23, 1, 0.32, 1)",
          }}
        >
          ご予約・お問い合わせ
        </h2>

        <p
          className="text-white/50 text-sm leading-relaxed mb-12"
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(25px)",
            transition: "all 0.8s 0.35s cubic-bezier(0.23, 1, 0.32, 1)",
          }}
        >
          お電話にてご予約を承っております。<br />
          お気軽にお問い合わせください。
        </p>

        {/* Phone Button */}
        <div
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(30px)",
            transition: "all 0.8s 0.5s cubic-bezier(0.23, 1, 0.32, 1)",
          }}
        >
          <a
            href={`tel:${salonInfo.phone}`}
            className="inline-flex items-center gap-3 whitespace-nowrap cursor-pointer"
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              fontSize: "clamp(1rem, 2vw, 1.2rem)",
              fontWeight: 400,
              letterSpacing: "0.2em",
              color: hovered ? "#E8C49A" : "#D4A373",
              transition: "color 0.3s ease",
              textDecoration: "none",
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            {/* Phone icon */}
            <span
              className="flex items-center justify-center w-5 h-5"
              style={{
                opacity: hovered ? 1 : 0.7,
                transition: "opacity 0.3s ease",
              }}
            >
              <Phone size={16} />
            </span>

            {/* Number with underline */}
            <span className="relative pb-px">
              {salonInfo.phone}
              <span
                className="absolute bottom-0 left-0 h-px bg-[#D4A373]"
                style={{
                  width: hovered ? "100%" : "0%",
                  transition: "width 0.35s cubic-bezier(0.23, 1, 0.32, 1)",
                }}
              />
            </span>

            {/* Arrow */}
            <span
              className="flex items-center justify-center w-4 h-4"
              style={{
                transform: hovered ? "translateX(5px)" : "translateX(0)",
                opacity: hovered ? 1 : 0.5,
                transition: "transform 0.3s ease, opacity 0.3s ease",
              }}
            >
              <ArrowRight size={14} />
            </span>
          </a>
        </div>

        {/* Hours note */}
        <p
          className="mt-6 text-white/30 text-xs tracking-widest"
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            opacity: inView ? 1 : 0,
            transition: "opacity 0.8s 0.7s",
          }}
        >
          受付時間：{salonInfo.hours}
        </p>
      </div>
    </section>
  );
}
